import { alpha, Box, Grid, Paper, Typography, useTheme } from "@mui/material";
import { useColorMode } from "~/providers/color-mode";
import {
  CanCreate,
  useCanCreate,
} from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Create";
import { IntegrationType } from "~/operations";
import { Link as RouterLink } from "react-router-dom";
import { TRANSITION_SPEED } from "~/pages/integrations/utils/integrationConstants";
import { IntegrationDynamicIcon } from "~/pages/integrations/components/IntegrationDynamicIcon";
import { ContentFlag } from "~/components/content-flag";
import { AvailableIntegration } from "~/pages/integrations/components/available-integrations/available-integration-cards-list/AvailableIntegrationCard";
import { GradientArrow } from "~/components/GradientArrow/GradientArrow";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";

interface AvailableRecommendedIntegrationCardProps {
  scope: SpaceScope | OrganizationScope;
  integration: AvailableIntegration;
}

export const AvailableRecommendedIntegrationCard = ({
  scope,
  integration,
}: AvailableRecommendedIntegrationCardProps) => {
  const href = `/space/integrations/add/${integration.id}?${scope.params}`;
  const theme = useTheme();
  const { mode } = useColorMode();
  const canCreateIntegration = useCanCreate();

  const mapIdToIntegrationType = (title: string) => {
    switch (title.toLowerCase()) {
      case "mondoo/aws":
      case "amazon aws":
      case "aws":
        return IntegrationType.Aws;
      case "mondoo/azure":
      case "azure":
        return IntegrationType.Azure;
      case "mondoo/kubernetes":
      case "kubernetes":
      case "k8s":
        return IntegrationType.K8S;
      case "mondoo/local":
      case "managed":
      case "managed_client":
        return IntegrationType.ManagedClient;
      default:
        return IntegrationType.ManagedClient;
    }
  };
  return (
    <Box
      {...(canCreateIntegration ? { component: RouterLink, to: href } : {})}
      height="100%"
      data-name={`add-recommended-integration-${integration.title.replaceAll(
        " ",
        "-",
      )}`}
    >
      <Paper
        sx={{
          height: 1,
          p: 3,
          backgroundColor: mode === "light" ? "" : "background.light",
          transition: TRANSITION_SPEED,
          "&:hover": {
            transform: "scale(1.05)",
            transition: TRANSITION_SPEED,
          },
        }}
      >
        <Grid container columnGap={3}>
          <Grid sx={{ minWidth: 50 }}>
            <IntegrationDynamicIcon
              {...{
                type: mapIdToIntegrationType(integration.title),
                sx: { fontSize: 48 },
              }}
            />
          </Grid>
          <Grid size="grow">
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Box>
                <Typography
                  fontSize={24}
                  fontWeight="fontWeightBold"
                  lineHeight={"24px"}
                >
                  {integration.title}
                </Typography>
                <ContentFlag
                  flag="recommended"
                  color="recommended"
                  layout={{
                    mt: 1,
                    ...(mode === "light"
                      ? {
                          color: "text.warning",
                          background: alpha(theme.palette.warning.main, 0.5),
                        }
                      : {}),
                  }}
                />
              </Box>

              <CanCreate>
                <GradientArrow />
              </CanCreate>
            </Box>
          </Grid>
        </Grid>
        <Typography color="text.secondary">
          {integration.description}
        </Typography>
      </Paper>
    </Box>
  );
};
